import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Plus, Trash2, Info, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTranslation } from 'react-i18next';
import { usePartnershipsAccess, usePartnershipsGovernance, useCommunityPicker, useSaveGovernance, BASE_VIEWER_TEAMS, type EditScope } from '@/hooks/usePartnerships';

const SCOPE_OPTIONS: EditScope[] = ['all', 'cases', 'faq'];

// Función de `t`: los textos salen de i18n y `t` no existe a nivel de módulo.
const SCOPE_LABELS = (t: (k: string) => string): Record<EditScope, string> => ({
  all: t('partnerships.partnershipsgovernance.scope_all'),
  cases: t('partnerships.partnershipsgovernance.scope_cases'),
  faq: t('partnerships.partnershipsgovernance.scope_faq'),
});

interface EditorRow {
  email: string;
  scope: EditScope;
}

export function PartnershipsGovernancePage() {
  const { t } = useTranslation();
  const { data: access, isLoading: accessLoading } = usePartnershipsAccess();
  const canAdmin = access?.editScopes?.includes('all') ?? false;

  const { data: governance, isLoading: govLoading, isError: govError } = usePartnershipsGovernance();
  const { data: members = [], isLoading: membersLoading } = useCommunityPicker();
  const save = useSaveGovernance();

  // ── Estado local editable ──────────────────────────────────────────────────
  const [viewerTeams, setViewerTeams] = useState<string[]>([]);
  const [editors, setEditors] = useState<EditorRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerScope, setPickerScope] = useState<EditScope>('all');
  const [addTeam, setAddTeam] = useState('');

  useEffect(() => {
    if (governance) {
      setViewerTeams(governance.viewerTeams ?? []);
      setEditors((governance.editors ?? []).map((e) => ({ email: e.email.toLowerCase(), scope: e.scope })));
      setDirty(false);
    }
  }, [governance]);

  // email → miembro del community map (para mostrar nombre / rol / área)
  const memberByEmail = useMemo(() => {
    const map = new Map<string, (typeof members)[number]>();
    for (const m of members) map.set(m.email.toLowerCase(), m);
    return map;
  }, [members]);

  const editorEmails = useMemo(() => new Set(editors.map((e) => e.email)), [editors]);

  // Equipos distintos del community map (para el selector de viewers)
  const availableTeams = useMemo(() => {
    const set = new Set<string>();
    for (const m of members) {
      if (m.team) set.add(m.team);
      if (m.area) set.add(m.area);
    }
    for (const t of viewerTeams) set.add(t);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [members, viewerTeams]);


  // ── Handlers ────────────────────────────────────────────────────────────────
  const addEditor = (email: string) => {
    setEditors((prev) => [...prev, { email: email.toLowerCase(), scope: pickerScope }]);
    setDirty(true);
  };
  const removeEditor = (email: string) => {
    setEditors((prev) => prev.filter((e) => e.email !== email));
    setDirty(true);
  };
  const changeScope = (email: string, scope: EditScope) => {
    setEditors((prev) => prev.map((e) => (e.email === email ? { ...e, scope } : e)));
    setDirty(true);
  };
  const addViewerTeam = (team: string) => {
    if (!team || viewerTeams.includes(team)) return;
    setViewerTeams((prev) => [...prev, team]);
    setAddTeam('');
    setDirty(true);
  };
  const removeViewerTeam = (team: string) => {
    if (BASE_VIEWER_TEAMS.includes(team)) return; // teams base: imborrables
    setViewerTeams((prev) => prev.filter((name) => name !== team));
    setDirty(true);
  };
  const handleSave = () => {
    save.mutate({ viewerTeams, editors }, { onSuccess: () => setDirty(false) });
  };

  // ── Estados de carga / acceso ────────────────────────────────────────────────
  if (accessLoading || govLoading) {
    return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  }

  if (!canAdmin || govError) {
    return (
      <div className="py-16 text-center space-y-4">
        <Shield className="h-10 w-10 mx-auto text-muted-foreground/40" />
        <p className="text-muted-foreground">{t('partnerships.partnershipsgovernance.no_tenes_acceso_al_panel_de')}</p>
        <Button variant="ghost" asChild>
          <Link to="/partnerships"><ArrowLeft className="h-4 w-4 mr-2" /> {t('partnerships.partnershipsgovernance.volver_al_hub')}</Link>
        </Button>
      </div>
    );
  }

  const grouped: Record<EditScope, EditorRow[]> = {
    all: editors.filter((e) => e.scope === 'all'),
    cases: editors.filter((e) => e.scope === 'cases'),
    faq: editors.filter((e) => e.scope === 'faq'),
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/partnerships" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Shield className="h-6 w-6 text-brand-green" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('partnerships.partnershipsgovernance.governance_de_permisos')}</h1>
            <p className="text-sm text-muted-foreground">{t('partnerships.partnershipsgovernance.quien_ve_y_quien_edita_el')}</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={!dirty || save.isPending}>
          {save.isPending ? t('partnerships.partnershipsgovernance.saving') : t('partnerships.partnershipsgovernance.guardar_cambios')}
        </Button>
      </div>

      {/* Viewers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('partnerships.partnershipsgovernance.acceso_de_lectura_viewers')}</CardTitle>
          <CardDescription>
            {t('partnerships.partnershipsgovernance.todos_los_que_pertenecen_a_estos')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {viewerTeams.map((team) => {
              const locked = BASE_VIEWER_TEAMS.includes(team);
              return (
                <Badge key={team} variant="secondary" className="gap-1.5 pl-2.5 pr-1 py-1 text-sm">
                  {team}
                  {locked ? (
                    <span className="p-0.5 text-muted-foreground/70" title={t('partnerships.partnershipsgovernance.equipo_fijo_no_se_puede_quitar')}>
                      <Lock className="h-3 w-3" />
                    </span>
                  ) : (
                    <button
                      onClick={() => removeViewerTeam(team)}
                      className="rounded-full hover:bg-black/10 dark:hover:bg-white/10 p-0.5"
                      aria-label={`Quitar ${team}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              );
            })}
          </div>
          <div className="flex items-center gap-2 max-w-sm">
            <Select value={addTeam} onValueChange={(v) => addViewerTeam(v)}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={membersLoading ? t('partnerships.partnershipsgovernance.cargando_equipos') : t('partnerships.partnershipsgovernance.agregar_equipo')} />
              </SelectTrigger>
              <SelectContent>
                {availableTeams
                  .filter((teamName) => !viewerTeams.includes(teamName))
                  .map((teamName) => (
                    <SelectItem key={teamName} value={teamName}>{teamName}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Editores */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">{t('partnerships.partnershipsgovernance.editors_title')}</CardTitle>
            <CardDescription>
              {t('partnerships.partnershipsgovernance.personas_puntuales_que_pueden_editar_el')}
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setPickerOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> {t('partnerships.partnershipsgovernance.agregar_editor')}
          </Button>
        </CardHeader>
        <CardContent>
          {editors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t('partnerships.partnershipsgovernance.sin_editores_solo_los_admins_pueden')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('partnerships.partnershipsgovernance.col_name')}</TableHead>
                  <TableHead>{t('partnerships.partnershipsgovernance.col_role')}</TableHead>
                  <TableHead>{t('partnerships.partnershipsgovernance.area')}</TableHead>
                  <TableHead className="w-[170px]">{t('partnerships.partnershipsgovernance.col_scope')}</TableHead>
                  <TableHead className="w-[48px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {SCOPE_OPTIONS.flatMap((scope) =>
                  grouped[scope].map((ed) => {
                    const m = memberByEmail.get(ed.email);
                    const inactive = m && m.status && m.status !== 'Activo';
                    return (
                      <TableRow key={ed.email}>
                        <TableCell className="font-medium">
                          {m?.name || ed.email}
                          {!m && <span className="ml-2 text-xs text-amber-600">{t('partnerships.partnershipsgovernance.no_esta_en_el_community_map')}</span>}
                          {inactive && <span className="ml-2 text-xs text-amber-600">({m!.status})</span>}
                          <div className="text-xs text-muted-foreground">{ed.email}</div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{m?.role || '—'}</TableCell>
                        <TableCell className="text-muted-foreground">{m?.area || m?.team || '—'}</TableCell>
                        <TableCell>
                          <Select value={ed.scope} onValueChange={(v) => changeScope(ed.email, v as EditScope)}>
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SCOPE_OPTIONS.map((s) => (
                                <SelectItem key={s} value={s}>{SCOPE_LABELS(t)[s]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeEditor(ed.email)}
                            aria-label={t('partnerships.partnershipsgovernance.quitar_editor')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        {t('partnerships.partnershipsgovernance.los_admins_de_la_plataforma_siempre')}
      </p>

      {/* Picker de miembros */}
      <MemberPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={t('partnerships.partnershipsgovernance.agregar_editor')}
        description={t('partnerships.partnershipsgovernance.elegi_el_alcance_y_hace_clic')}
        searchPlaceholder={t('partnerships.partnershipsgovernance.buscar_por_nombre_mail_rol_o')}
        noResultsText={t('partnerships.partnershipsgovernance.sin_resultados')}
        members={members}
        loading={membersLoading}
        exclude={editorEmails}
        onPick={(m) => addEditor(m.email)}
      >
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground shrink-0">{t('partnerships.partnershipsgovernance.scope_prefix')}</span>
              <Select value={pickerScope} onValueChange={(v) => setPickerScope(v as EditScope)}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{SCOPE_LABELS(t)[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
        </MemberPickerDialog>
    </div>
  );
}
